import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl-next';

import Form from '@atlaskit/form';
import { Box, Stack, Text } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';

import {
	useAiBulkNavigationCurrentPage,
	useAiBulkNavigationGotoPage,
} from './providers/AiBulkNavigationContextProvider';
import { BulkConfigureFooter } from './BulkConfigureFooter';
import { useBulkCreateContext } from './providers/BulkCreateContextProvider';
import { BulkCreateNavigationHeader } from './BulkCreateNavigationHeader';
import { messages } from './messages';
import { SiteSelector } from './SiteSelector';
import { ProjectSelector } from './ProjectSelector';
import { DefaultIssueTypeSelector } from './DefaultIssueTypeSelector';
import { SidePanelWrapper } from './SidePanelWrapper';
import {
	useAvailableSitesState,
	useIsConfigureLoading,
	useIssueTypesState,
	useProjectsActions,
	useProjectsState,
	useResetConfigureState,
} from './providers/BulkConfigureContextProvider';
import { saveJiraConfigDetailsToLocalStorage } from './utils/localStorageHelpers';

// This is the view for both AI and non-AI bulk create where users can edit and create individual issues
export const BulkConfigureView = () => {
	const [
		{ currentJiraSite, currentJiraProject, currentDefaultIssueType },
		{ setCurrentJiraSite, setCurrentJiraProject, setCurrentDefaultIssueType },
	] = useBulkCreateContext();

	const { selectedAvailableSite } = useAvailableSitesState();
	const { selectedProject } = useProjectsState();
	const { selectedIssueType } = useIssueTypesState();
	const { isLoading, isJiraError } = useIsConfigureLoading();
	const resetConfigureState = useResetConfigureState();
	const { setIsJiraError } = useProjectsActions();

	const goToPage = useAiBulkNavigationGotoPage();
	const currentPage = useAiBulkNavigationCurrentPage();

	const isOpen = currentPage === 'configure';

	const onSubmit = useCallback(() => {
		selectedAvailableSite && void setCurrentJiraSite(selectedAvailableSite);
		selectedProject && void setCurrentJiraProject(selectedProject);
		selectedIssueType && void setCurrentDefaultIssueType(selectedIssueType);
		saveJiraConfigDetailsToLocalStorage({
			selectedSite: selectedAvailableSite,
			selectedProject,
			selectedIssueType,
		});
		void goToPage('default');
	}, [
		selectedAvailableSite,
		setCurrentJiraSite,
		selectedProject,
		setCurrentJiraProject,
		selectedIssueType,
		setCurrentDefaultIssueType,
		goToPage,
	]);

	const onClose = useCallback(() => {
		if (currentJiraSite && currentJiraProject && currentDefaultIssueType) {
			void resetConfigureState(currentJiraSite, currentJiraProject, currentDefaultIssueType);
		}
		void setIsJiraError(false);
		void goToPage('default');
	}, [
		currentDefaultIssueType,
		currentJiraProject,
		currentJiraSite,
		goToPage,
		resetConfigureState,
		setIsJiraError,
	]);

	const isSaveDisabled =
		isLoading || !selectedAvailableSite || !selectedProject || !selectedIssueType;

	return (
		<SidePanelWrapper isOpen={isOpen}>
			<Box padding="space.200">
				<Stack space="space.100">
					<BulkCreateNavigationHeader onClose={onClose}>
						<FormattedMessage {...messages.bulkConfigureHeaderText} />
					</BulkCreateNavigationHeader>
					<Text color="color.text.subtlest">
						<FormattedMessage {...messages.bulkCreateConfigureBodyText} />
					</Text>
					{isJiraError && (
						<SectionMessage appearance="error" testId="bulkCreateErrorSectionMessage">
							<FormattedMessage {...messages.bulkConfigureJiraErrorText} />
						</SectionMessage>
					)}
					<Form onSubmit={onSubmit}>
						{({ formProps }) => (
							<form id="configureForm" {...formProps}>
								<SiteSelector />
								<ProjectSelector />
								<DefaultIssueTypeSelector />
							</form>
						)}
					</Form>
				</Stack>
			</Box>
			<BulkConfigureFooter onClose={onClose} isSaveDisabled={isSaveDisabled} />
		</SidePanelWrapper>
	);
};
